'use client'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useAppShellStore } from '@bitechsystems/btappshell'
import {
  Grid,
  Card,
  Group,
  Title,
  ScrollArea,
  Timeline,
  Text,
  Avatar,
  useMantineTheme,
  MantineTheme,
  Box,
  Stack,
  Tabs,
  Transition,
} from '@mantine/core'

import { MdPublishedWithChanges } from 'react-icons/md'
import { shallow } from 'zustand/shallow'
import { useHomeStatsQueryData } from './useHomeStatsQueryData'
import { Bar } from 'react-chartjs-2'

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
} from 'chart.js'
import { toHumanCase } from '@bitechsystems/btutils'
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ChartTitle,
  Tooltip,
  Legend
)

const UserTimeLine = ({ lastchanges }: any) => {
  return (
    <Timeline active={1} bulletSize={24} lineWidth={2}>
      {React.Children.toArray(
        lastchanges?.map((v) => {
          return (
            <Timeline.Item title={`${v?.action} ${v?.entitytype}`}>
              <Text color='dimmed' size='sm'>
                {v?.entity}
                <Text variant='link' component='span' inherit></Text>{' '}
              </Text>
              <Text size='xs' mt={4}>
                {v?.timestamp}
              </Text>
            </Timeline.Item>
          )
        })
      )}
    </Timeline>
  )
}

// <List
//                   size={'xs'}
//                   styles={{
//                     item: { color: theme.colors.dark, fontWeight: 500 },
//                   }}
//                 >
//                   {React.Children.toArray(
//                     query.data?.[0]?.p_data?.lastchanges?.map((v) => {
//                       return (
//                         <List.Item>
//                           {v?.timestamp}: {v?.action} {v?.entity}
//                         </List.Item>
//                       )
//                     })
//                   )}
//                 </List>

export const UserCard = ({
  theme,
  classes,
}: {
  theme: MantineTheme
  classes: any
}) => {
  const [userDetail] = useAppShellStore((state) => [state.userDetail], shallow)
  const [statsData, statsDataLoaded, updatedAt] = useHomeStatsQueryData()

  const chartOptions = {
    responsive: true,
    color:
      theme.colorScheme == 'dark' ? theme.colors.gray[0] : theme.colors.gray[8],
    borderColor:
      theme.colorScheme == 'dark' ? theme.colors.gray[0] : theme.colors.gray[8],

    pointLabels: {
      fontColor:
        theme.colorScheme == 'dark'
          ? theme.colors.gray[0]
          : theme.colors.gray[8],
    },
    gridLines: {
      color:
        theme.colorScheme == 'dark'
          ? theme.colors.gray[0]
          : theme.colors.gray[8],
    },
    angleLines: {
      color:
        theme.colorScheme == 'dark'
          ? theme.colors.gray[0]
          : theme.colors.gray[8],
    },
    legend: {
      labels: {
        fontColor:
          theme.colorScheme == 'dark'
            ? theme.colors.gray[0]
            : theme.colors.gray[8],
      },
    },
    plugins: {
      title: {
        display: true,
        text: 'Assigned to you',
        fontColor:
          theme.colorScheme == 'dark'
            ? theme.colors.gray[0]
            : theme.colors.gray[8],
      },
    },
  }

  const usersChartOptions = {
    ...chartOptions,
    plugins: {
      title: {
        display: true,
        text: 'Active Users',
        fontColor:
          theme.colorScheme == 'light'
            ? theme.colors.gray[0]
            : theme.colors.gray[8],
      },
    },
  }

  const chartData: any = {
    labels: ['Actions', 'Tasks'],

    datasets: [
      {
        label: 'Open Actions',
        data: [statsData.actions?.open, statsData.tasks?.open],
        //borderColor: 'rgb(255, 99, 132)',
        backgroundColor: theme.colors.red[4],
      },
      {
        label: 'Done Actions',
        data: [statsData.actions?.done, statsData.tasks?.done],

        backgroundColor: theme.colors.green[2],
      },
    ],
  }

  const getChartDataComms = (type: string) => {
    return {
      labels: statsData.comms?.[type]?.['period'],

      datasets: Object.keys(statsData.comms?.email ?? {})
        .filter((f) => f != 'period')
        .map((v) => {
          let color = theme.colors.gray[4]
          color = v == 'open' ? theme.colors.yellow[5] : color
          color = v == 'received' ? theme.colors.orange[4] : color
          color = v == 'sent' ? theme.colors.blue[4] : color
          color = v == 'errors' ? theme.colors.red[4] : color

          return {
            label: toHumanCase(v),
            data: statsData.comms?.[type]?.[v],
            backgroundColor: color,
          }
        }),
    }
  }

  const chartOptionsComms = {
    ...chartOptions,
    plugins: {
      title: {
        display: false,
        text: '',
        fontColor:
          theme.colorScheme == 'light'
            ? theme.colors.gray[0]
            : theme.colors.gray[8],
      },
    },
  }

  const usersChartData: any = {
    labels: ['Online'],

    datasets: [
      {
        label: 'Users',
        data: [statsData.sessions?.users],

        backgroundColor: theme.colors.green[2],
      },
      {
        label: 'Sessions',
        data: [statsData.sessions?.sessions],

        backgroundColor: theme.colors.green[6],
      },
    ],
  }

  return (
    <Card shadow={'xs'} mt={'lg'}>
      <Card.Section px={'xl'} py='xs' withBorder>
        <Group spacing={'xs'} grow>
          <Group position='left'>
            <Title
              weight={550}
              order={5}
              color={
                theme.colorScheme == 'dark'
                  ? theme.colors.dark[0]
                  : theme.colors.dark[5]
              }
            >
              Welcome to Simplicity. You are logged in as
            </Title>
            <Title
              weight={600}
              order={5}
              color={
                theme.colorScheme == 'dark'
                  ? theme.colors.dark[0]
                  : theme.colors.dark[5]
              }
            >
              {userDetail.username}
            </Title>
            <Avatar
              color={theme.primaryColor}
              radius='xl'
              size='sm'
              alt={userDetail.description ?? userDetail.username}
            >
              {userDetail.username.substring(0, 2)}
            </Avatar>
          </Group>
          <Group position='right'>
            <TimeCnt />
          </Group>
        </Group>
      </Card.Section>

      <Card.Section p={'xs'}>
        <Transition
          mounted={statsDataLoaded}
          transition='slide-right'
          duration={1000}
          timingFunction='ease'
        >
          {(styles) => (
            <Grid p='sm' gutter={'lg'} styles={styles}>
              <Grid.Col span={4}>
                <Box style={{ height: '170px', width: '100%' }}>
                  <Bar data={chartData} options={chartOptions} />
                </Box>
              </Grid.Col>
              <Grid.Col span={4}>
                <Box style={{ height: '170px', width: '100%' }}>
                  <Tabs
                    defaultValue='email'
                    keepMounted={false}
                    loop
                    radius='xs'
                    variant='outline'
                  >
                    <Tabs.List grow>
                      <Tabs.Tab value='email'>Email</Tabs.Tab>
                      <Tabs.Tab value='sms'>SMS</Tabs.Tab>
                      <Tabs.Tab value='call'>Call</Tabs.Tab>
                      <Tabs.Tab value='whatsapp'>Whatsapp</Tabs.Tab>
                    </Tabs.List>

                    <Tabs.Panel value='email'>
                      <Bar
                        data={getChartDataComms('email')}
                        options={chartOptionsComms}
                      />
                    </Tabs.Panel>
                    <Tabs.Panel value='sms'>
                      <Bar
                        data={getChartDataComms('sms')}
                        options={chartOptionsComms}
                      />
                    </Tabs.Panel>
                    <Tabs.Panel value='call'>
                      <Bar
                        data={getChartDataComms('call')}
                        options={chartOptionsComms}
                      />
                    </Tabs.Panel>
                    <Tabs.Panel value='whatsapp'>
                      <Bar
                        data={getChartDataComms('whatsapp')}
                        options={chartOptionsComms}
                      />
                    </Tabs.Panel>
                  </Tabs>
                </Box>
              </Grid.Col>
              <Grid.Col span={4}>
                <Box style={{ height: '170px', width: '100%' }}>
                  <Bar data={usersChartData} options={usersChartOptions} />
                </Box>
              </Grid.Col>

              {/*
          <Grid.Col span={6}>
            <Title order={5}>
              <FaBell color={theme.colors.blue[7]} />
              Assigned / Todo
            </Title>
            <Group>
              <List
                size={'xs'}
                styles={{
                  item: { color: theme.colors.dark, fontWeight: 500 },
                }}
              >
                <List.Item>
                  {statsData?.actions?.done}/{statsData?.actions?.done} Actions
                </List.Item>
                <List.Item>
                  {statsData?.tasks?.done}/{statsData?.tasks?.done} Tasks
                </List.Item>
                <List.Item>0 Communications</List.Item>
              </List>
            </Group>
            <Title order={5}>Due</Title>
            <Group>
              <BiTimer color={theme.colors.blue[7]} />
              <List
                size={'xs'}
                styles={{
                  item: { color: theme.colors.dark, fontWeight: 500 },
                }}
              >
                <List.Item>0 Actions overdue</List.Item>
              </List>
            </Group>
          </Grid.Col> */}
            </Grid>
          )}
        </Transition>
      </Card.Section>
      <Text size='xs'>
        {updatedAt != ''
          ? 'Last Updated: ' + updatedAt.replace('T', ' ').substring(10, 19)
          : ''}{' '}
      </Text>
    </Card>
  )
}

export const TimeCnt = () => {
  const [tm, setTm] = useState('')
  const refInterval = useRef<any>()

  const calcTime = React.useCallback(() => {
    const now = new Date()
    setTm(now.toISOString().replace('T', ' ').substring(0, 19))
  }, [])

  React.useEffect(() => {
    refInterval.current = setInterval(calcTime, 1000)

    return () => {
      if (refInterval.current) {
        clearInterval(refInterval.current)
        refInterval.current = undefined
      }
    }
  }, [])

  return <Text>{tm}</Text>
}

export const UserChangesComp = ({
  theme,
  classes,
  height,
}: {
  theme: MantineTheme
  classes: any
  height?: number
}) => {
  const [statsData] = useHomeStatsQueryData()

  return (
    <Stack>
      <Title order={5} p='sm'>
        <MdPublishedWithChanges color={theme.colors.blue[7]} /> My Latest
        Activities
      </Title>
      <Group>
        <ScrollArea.Autosize
          maxHeight={1024}
          style={{
            height: height ?? '100%',
            width: '100%',
            maxHeight: '100vh',
          }}
        >
          <UserTimeLine lastchanges={statsData?.lastchanges ?? []} />
        </ScrollArea.Autosize>
      </Group>
    </Stack>
  )
}
